import React from "react";

import { useHistory } from "react-router";

import { Button } from "@/design-system/components/Button/Button";
import AiChatApprovalFaq from "./AiChatApprovalFaq";
import useAiChatApprovalStatus from "@/ai-chat/hooks/useAiChatApprovalStatus";
import useApproveAiChat from "@/ai-chat/hooks/useApproveAiChat";
import useUsers from "@/hooks/useUsers";
import { userIsCROAdmin } from "@/util";

type Props = {
  children: React.ReactNode;
};

export default function AiChatApprovalGate({ children }: Props) {
  const history = useHistory();
  const { data: approvalStatus, isLoading } = useAiChatApprovalStatus();
  const approveMutation = useApproveAiChat();
  const usersState = useUsers();
  const isAdmin = userIsCROAdmin(usersState.currentUser);

  const approved = approvalStatus?.approved ?? false;
  const showModal = !approved && !isLoading;

  return (
    <div className="relative -m-8 h-[calc(100%+4rem)] min-h-0">
      <div
        className={
          approved
            ? "h-full p-8"
            : "pointer-events-none h-full p-8 opacity-50 select-none"
        }
        aria-hidden={!approved}
      >
        {children}
      </div>
      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enable Freshpaint AI"
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/25"
        >
          <div className="mx-4 flex max-h-[80vh] w-[36rem] flex-col rounded-md bg-white shadow-lg">
            <div className="flex flex-none items-center justify-between border-b px-6 py-3">
              <h2 className="text-[18px] font-semibold">
                Enable Freshpaint AI
              </h2>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <div className="text-textHighEmphasis space-y-3 text-sm">
                <p>
                  By clicking <strong>Enable Freshpaint AI</strong>, you confirm
                  that you are authorized by your organization to enable
                  Freshpaint AI and acknowledge that your data may be processed
                  as described below. If you click <strong>Cancel</strong>,
                  Freshpaint AI-powered features will remain off for your
                  account.
                </p>
                <p>
                  Freshpaint AI may process the data you submit to, or make
                  available through, the Freshpaint platform to generate
                  customer-specific insights and outputs. This processing occurs
                  within Freshpaint-managed environments and, where applicable,
                  through Anthropic&rsquo;s Claude models, and is used only to
                  provide Freshpaint AI functionality for your account.
                  Freshpaint and its AI subprocessors do not use your data to
                  train underlying AI models.
                </p>
                <p>
                  Freshpaint AI is intended to assist your analysis and does not
                  replace professional judgment or review of the underlying
                  data. You are responsible for independently reviewing and
                  validating Freshpaint AI outputs before relying on, using, or
                  sharing them.
                </p>
              </div>
              {isAdmin ? (
                <AiChatApprovalFaq />
              ) : (
                <p className="text-textHighEmphasis mt-4 text-sm">
                  An account admin must enable AI chat before it can be used.
                  Please contact your admin to enable this feature.
                </p>
              )}
            </div>

            {isAdmin && (
              <div className="flex flex-none items-center justify-end gap-3 border-t px-6 py-4">
                {approveMutation.isError && (
                  <p className="mr-auto text-sm text-red-600">
                    Failed to enable AI chat. Please try again.
                  </p>
                )}
                <Button
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => history.push("/")}
                >
                  Cancel
                </Button>
                <Button
                  className="cursor-pointer"
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isLoading}
                  loading={approveMutation.isLoading}
                >
                  {approveMutation.isLoading
                    ? "Enabling..."
                    : "Enable Freshpaint AI"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
